import React from 'react';
import { Box, useTheme } from '@mui/material';

interface ReadingTypeProps {
    title?: string;
    description?: string;
    imageUrl?: string;
    type?: string;
    setup?: number[];
    width?: string;
    height?: string;
}

// Position data for Celtic Cross thumbnail (10 cards)
// Scaled down positions inspired by the full Celtic Cross spread
const celticCrossPositions = [
    { left: 35, top: 35, width: 12, height: 18 },      // 0: Present situation (center, slightly left)
    { left: 35, top: 35, width: 12, height: 18 },      // 1: Challenge (crossing card, slightly right)
    { left: 35, top: 8, width: 12, height: 18 },       // 2: Focus (above center)
    { left: 10, top: 35, width: 12, height: 18 },      // 3: Recent Past (left)
    { left: 35, top: 62, width: 12, height: 18 },      // 4: Possible Outcome (below center)
    { left: 58, top: 35, width: 12, height: 18 },      // 5: Near Future (right)
    { left: 75, top: 75, width: 12, height: 18 },      // 6: Your Approach (bottom right)
    { left: 75, top: 55, width: 12, height: 18 },      // 7: External Influences (middle right)
    { left: 75, top: 35, width: 12, height: 18 },      // 8: Hopes & Fears (top right)
    { left: 75, top: 15, width: 12, height: 18 }       // 9: Final Outcome (top right)*/
];

// Position data for Horseshoe spread thumbnail (7 cards)
// Scaled down positions arranged in a U-shape from left to right
const horseshoePositions = [
    { left: 5, top: 65, width: 12, height: 18 },       // 0: Past influences (bottom left)
    { left: 18, top: 45, width: 12, height: 18 },      // 1: Present situation (left side)
    { left: 25, top: 25, width: 12, height: 18 },      // 2: Hidden influences (top left)
    { left: 42, top: 15, width: 12, height: 18 },      // 3: Current situation (center top)
    { left: 60, top: 25, width: 12, height: 18 },      // 4: Challenges/obstacles (top right)
    { left: 67, top: 45, width: 12, height: 18 },      // 5: Possible outcome (right side)
    { left: 80, top: 65, width: 12, height: 18 }       // 6: Final outcome (bottom right)
];

// Position data for Tetraktys spread thumbnail (10 cards)
// Scaled down positions arranged in a pyramid formation (1-2-3-4)
const tetraktysPositions = [
    // Row 1: 1 card (top center)
    { left: 44, top: 10, width: 12, height: 18 },      // 0: Spiritual essence (top)

    // Row 2: 2 cards
    { left: 34, top: 25, width: 12, height: 18 },      // 1: Intuition (left)
    { left: 54, top: 25, width: 12, height: 18 },      // 2: Logic (right)

    // Row 3: 3 cards
    { left: 24, top: 40, width: 12, height: 18 },      // 3: Past influences
    { left: 44, top: 40, width: 12, height: 18 },      // 4: Present situation
    { left: 64, top: 40, width: 12, height: 18 },      // 5: Future potential

    // Row 4: 4 cards (bottom row)
    { left: 14, top: 55, width: 12, height: 18 },      // 6: Material world
    { left: 34, top: 55, width: 12, height: 18 },      // 7: Emotional realm
    { left: 54, top: 55, width: 12, height: 18 },      // 8: Mental realm
    { left: 74, top: 55, width: 12, height: 18 }       // 9: Spiritual realm
];

// Position data for Tree of Life spread thumbnail (10 cards)
// Scaled down positions arranged in the Tree of Life formation
const treeOfLifePositions = [
    // Card 1 - Intention (top right)
    { left: 60, top: 15, width: 12, height: 18 },

    // Card 2 - Potential (top left)
    { left: 28, top: 15, width: 12, height: 18 },

    // Card 3 - Expression (middle right)
    { left: 60, top: 35, width: 12, height: 18 },

    // Card 4 - Assistance (middle left)
    { left: 28, top: 35, width: 12, height: 18 },

    // Card 5 - Inner Work (bottom right)
    { left: 60, top: 60, width: 12, height: 18 },

    // Card 6 - Positive Encouragement (bottom left)
    { left: 28, top: 60, width: 12, height: 18 },

    // Card 7 - Emotional Aspects (bottom center)
    { left: 44, top: 75, width: 12, height: 18 },

    // Card 8 - Thoughts (bottom center left)
    { left: 44, top: 65, width: 12, height: 18 },

    // Card 9 - The Unknown (center middle)
    { left: 44, top: 45, width: 12, height: 18 },

    // Card 10 - Likely Outcome (top center)
    { left: 44, top: 5, width: 12, height: 18 }
];

// Position data for Astrological spread thumbnail (13 cards: 12 in circle + 1 center)
// Scaled down positions arranged in a circle formation representing the 12 astrological houses with center card
const generateAstrologicalThumbnailPositions = (): Array<{ left: number, top: number, width: number, height: number }> => {
    const positions = [];
    const centerX = 50; // Center of the circle (percentage)
    const centerY = 50; // Center of the circle (percentage)
    const radius = 30; // Radius of the circle (percentage from center)
    const cardWidth = 8; // Card width in percentage
    const cardHeight = 12; // Card height in percentage

    // Calculate positions for 12 cards in a circle
    // Start at 9 o'clock position (270 degrees) and move counterclockwise
    for (let i = 0; i < 12; i++) {
        // Calculate angle for each card (starting at 270 degrees, moving counterclockwise)
        const angle = (270 + (i * 30)) * (Math.PI / 180); // 30 degrees between each card

        // Calculate x and y positions
        const x = centerX + (radius * Math.cos(angle)) - (cardWidth / 2);
        const y = centerY + (radius * Math.sin(angle)) - (cardHeight / 2);

        positions.push({
            left: x,
            top: y,
            width: cardWidth,
            height: cardHeight
        });
    }

    // Add the 13th card in the center
    positions.push({
        left: centerX - (cardWidth / 2),
        top: centerY - (cardHeight / 2),
        width: cardWidth,
        height: cardHeight
    });

    return positions;
};

const astrologicalPositions = generateAstrologicalThumbnailPositions();

// Position data for Year Ahead spread thumbnail (12 cards in circle)
// Scaled down positions arranged in a circle formation representing the 12 months
const generateYearAheadThumbnailPositions = (): Array<{ left: number, top: number, width: number, height: number }> => {
    const positions = [];
    const centerX = 50; // Center of the circle (percentage)
    const centerY = 50; // Center of the circle (percentage)
    const radius = 30; // Radius of the circle (percentage from center)
    const cardWidth = 8; // Card width in percentage
    const cardHeight = 12; // Card height in percentage

    // Calculate positions for 12 cards in a circle
    // Start at 12 o'clock position (270 degrees) and move clockwise
    for (let i = 0; i < 12; i++) {
        // Calculate angle for each card (starting at 270 degrees, moving clockwise)
        const angle = (270 + (i * 30)) * (Math.PI / 180); // 30 degrees between each card

        // Calculate x and y positions
        const x = centerX + (radius * Math.cos(angle)) - (cardWidth / 2);
        const y = centerY + (radius * Math.sin(angle)) - (cardHeight / 2);

        positions.push({
            left: x,
            top: y,
            width: cardWidth,
            height: cardHeight
        });
    }

    return positions;
};

const yearAheadPositions = generateYearAheadThumbnailPositions();

const ReadingType: React.FC<ReadingTypeProps> = ({
    title = "Reading Type Title",
    type,
    setup = [0, 1, 3, 4, 5, 6, 7, 8, 9],
    width = '100px',
    height = '100px'
}) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                width,
                height,
                borderRadius: 5,
                maxWidth: 150,
            }}
        >


            {(type === "3x1" || type === "3x3" || type === "9x9") && <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.2, padding: "10px" }}>
                {[...Array(9)].map((_, index) => (
                    <Box
                        key={index}
                        sx={{
                            aspectRatio: '2/3',
                            backgroundColor: setup.includes(index) ? theme.palette.background.dark : 'transparent',
                            borderRadius: '2px',
                            width: '18px',
                            height: '22px',
                            margin: '2px'
                        }}
                    ></Box>
                ))}
            </Box>}

            {type === "one-card-reading" && <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: "10px",
                width: '100%',
                height: '100%'
            }}>
                <Box
                    sx={{
                        aspectRatio: '2/3',
                        backgroundColor: theme.palette.background.dark,
                        borderRadius: '2px',
                        width: '18px',
                        height: '22px',
                    }}
                ></Box>
            </Box>}

            {type === "three-card" && <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 0.3,
                padding: "10px",
                justifyItems: 'center',
                alignItems: 'center'
            }}>
                {[...Array(3)].map((_, index) => (
                    <Box
                        key={index}
                        sx={{
                            aspectRatio: '2/3',
                            backgroundColor: theme.palette.background.dark,
                            borderRadius: '2px',
                            width: '18px',
                            height: '22px',
                        }}
                    ></Box>
                ))}
            </Box>}

            {type === "3-pyramid" && <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 0.2,
                padding: "10px",
                paddingLeft: "15px",

            }}>
                <Box
                    key={0}
                    sx={{
                        aspectRatio: '2/3',
                        backgroundColor: theme.palette.background.dark,
                        borderRadius: '2px',
                        width: '18px',
                        height: '22px',
                        margin: '2px',
                        gridColumn: '2'
                    }}
                ></Box>
                <Box
                    key={1}
                    sx={{
                        aspectRatio: '2/3',
                        backgroundColor: theme.palette.background.dark,
                        borderRadius: '2px',
                        width: '18px',
                        height: '22px',
                        margin: '2px',
                        gridColumn: '1',
                        gridRow: '2'
                    }}
                ></Box>
                <Box
                    key={2}
                    sx={{
                        aspectRatio: '2/3',
                        backgroundColor: theme.palette.background.dark,
                        borderRadius: '2px',
                        width: '18px',
                        height: '22px',
                        margin: '2px',
                        gridColumn: '3',
                        gridRow: '2'
                    }}
                ></Box>
            </Box>}

            {type === "9-pyramid" && <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                padding: "10px",
                paddingLeft: "20px",
                paddingTop: "30px"
            }}>
                <Box sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    gap: '10px'
                }}>
                    <Box sx={{
                        aspectRatio: '2/3',
                        backgroundColor: '#1c4a3d',
                        borderRadius: '2px',
                        width: '18px',
                        height: '22px',
                    }} />

                </Box>
                <Box sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    gap: '10px'
                }}>
                    <Box sx={{
                        aspectRatio: '2/3',
                        backgroundColor: '#1c4a3d',
                        borderRadius: '2px',
                        width: '18px',
                        height: '22px',
                    }} />
                    <Box sx={{
                        aspectRatio: '2/3',
                        backgroundColor: '#1c4a3d',
                        borderRadius: '2px',
                        width: '18px',
                        height: '22px',
                    }} />
                </Box>
                <Box sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    gap: '10px'
                }}>
                    <Box sx={{
                        aspectRatio: '2/3',
                        backgroundColor: '#1c4a3d',
                        borderRadius: '2px',
                        width: '18px',
                        height: '22px',
                    }} />
                    <Box sx={{
                        aspectRatio: '2/3',
                        backgroundColor: '#1c4a3d',
                        borderRadius: '2px',
                        width: '18px',
                        height: '22px',
                    }} />
                    <Box sx={{
                        aspectRatio: '2/3',
                        backgroundColor: '#1c4a3d',
                        borderRadius: '2px',
                        width: '18px',
                        height: '22px',
                    }} />
                </Box>
            </Box>}

            {type === "celtic-cross" && <Box sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                padding: "5px",
                overflow: 'visible'
            }}>
                {celticCrossPositions.map((position, cardIndex) => (
                    <Box
                        key={`celtic-cross-thumb-${cardIndex}`}
                        sx={{
                            position: 'absolute',
                            left: `${position.left}%`,
                            top: `${position.top}%`,
                            width: `${position.width}%`,
                            height: `${position.height}%`,
                            aspectRatio: '2/3',
                            backgroundColor: theme.palette.background.dark,
                            borderRadius: '1px',
                            zIndex: cardIndex === 1 ? 2 : 1, // Challenge card on top
                            transform: cardIndex === 1 ? 'rotate(90deg)' : 'none', // Rotate the crossing card
                        }}
                    />
                ))}
            </Box>}

            {type === "horseshoe-spread" && <Box sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                padding: "5px",
                overflow: 'visible'
            }}>
                {horseshoePositions.map((position, cardIndex) => (
                    <Box
                        key={`horseshoe-thumb-${cardIndex}`}
                        sx={{
                            position: 'absolute',
                            left: `${position.left}%`,
                            top: `${position.top}%`,
                            width: `${position.width}%`,
                            height: `${position.height}%`,
                            aspectRatio: '2/3',
                            backgroundColor: theme.palette.background.dark,
                            borderRadius: '1px',
                            zIndex: 1,
                        }}
                    />
                ))}
            </Box>}

            {(type === "tetraktys-spread" || type === "pyramid") && <Box sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                padding: "5px",
                overflow: 'visible'
            }}>
                {tetraktysPositions.map((position, cardIndex) => (
                    <Box
                        key={`tetraktys-thumb-${cardIndex}`}
                        sx={{
                            position: 'absolute',
                            left: `${position.left}%`,
                            top: `${position.top}%`,
                            width: `${position.width}%`,
                            height: `${position.height}%`,
                            aspectRatio: '2/3',
                            backgroundColor: theme.palette.background.dark,
                            borderRadius: '1px',
                            zIndex: 1,
                        }}
                    />
                ))}
            </Box>}

            {type === "tree-of-life" && <Box sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                padding: "5px",
                overflow: 'visible'
            }}>
                {treeOfLifePositions.map((position, cardIndex) => (
                    <Box
                        key={`tree-of-life-thumb-${cardIndex}`}
                        sx={{
                            position: 'absolute',
                            left: `${position.left}%`,
                            top: `${position.top}%`,
                            width: `${position.width}%`,
                            height: `${position.height}%`,
                            aspectRatio: '2/3',
                            backgroundColor: theme.palette.background.dark,
                            borderRadius: '1px',
                            zIndex: 1,
                        }}
                    />
                ))}
            </Box>}

            {(type === "astrological-spread" || type === "circle") && <Box sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                padding: "5px",
                overflow: 'visible'
            }}>
                {astrologicalPositions.map((position, cardIndex) => (
                    <Box
                        key={`astrological-thumb-${cardIndex}`}
                        sx={{
                            position: 'absolute',
                            left: `${position.left}%`,
                            top: `${position.top}%`,
                            width: `${position.width}%`,
                            height: `${position.height}%`,
                            aspectRatio: '2/3',
                            backgroundColor: theme.palette.background.dark,
                            borderRadius: '1px',
                            zIndex: 1,
                        }}
                    />
                ))}
            </Box>}

            {(type === "year-ahead-spread" || type === "year-ahead") && <Box sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                padding: "5px",
                overflow: 'visible'
            }}>
                {yearAheadPositions.map((position, cardIndex) => (
                    <Box
                        key={`year-ahead-thumb-${cardIndex}`}
                        sx={{
                            position: 'absolute',
                            left: `${position.left}%`,
                            top: `${position.top}%`,
                            width: `${position.width}%`,
                            height: `${position.height}%`,
                            aspectRatio: '2/3',
                            backgroundColor: theme.palette.background.dark,
                            borderRadius: '1px',
                            zIndex: 1,
                        }}
                    />
                ))}
            </Box>}
        </Box>

    );
};

export default ReadingType;
